import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import { CategoriesService, type CategoryWithGroup } from '../services/categories-service';

interface CategoryPickerProps {
  value: string | null;
  onChange: (categoryId: string | null, categoryName?: string) => void;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  groupTypeFilter?: 'income' | 'expense' | 'transfer';
}

interface CategoryOption {
  id: string;
  name: string;
  groupName: string;
  groupType: string;
  groupColor: string | null;
  groupIcon: string | null;
  isSystem: boolean;
}

export const CategoryPicker: React.FC<CategoryPickerProps> = ({
  value,
  onChange,
  label = 'Category',
  size = 'small',
  fullWidth = true,
  disabled = false,
  groupTypeFilter,
}) => {
  const [categories, setCategories] = useState<CategoryWithGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await CategoriesService.getCategories();
        setCategories(response.data.categories);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const options: CategoryOption[] = useMemo(() => {
    let filtered = categories;
    if (groupTypeFilter) {
      filtered = categories.filter((c) => c.group_type === groupTypeFilter);
    }
    return filtered.map((c) => ({
      id: c.id,
      name: c.name,
      groupName: c.group_name,
      groupType: c.group_type,
      groupColor: c.group_color || null,
      groupIcon: c.group_icon || null,
      isSystem: c.is_system,
    }));
  }, [categories, groupTypeFilter]);

  const selectedOption = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value]
  );

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      options={options}
      value={selectedOption}
      onChange={(_event, newValue) => {
        onChange(newValue?.id || null, newValue?.name);
      }}
      getOptionLabel={(option) => option.name}
      groupBy={(option) => option.groupName}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader
            sx={{
              backgroundColor: 'transparent',
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              lineHeight: '32px',
            }}
          >
            {params.group}
          </ListSubheader>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props as any;
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {option.groupColor && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: option.groupColor,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.name}
            size="small"
            sx={{
              bgcolor: option.groupColor ? `${option.groupColor}20` : undefined,
              borderColor: option.groupColor || undefined,
            }}
          />
        ))
      }
    />
  );
};
